import { Trans, useLingui } from '@lingui/react/macro';
import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { AlertCircle } from '@untitledui/icons/AlertCircle';
import { CheckCircle } from '@untitledui/icons/CheckCircle';
import { useEffect, useState } from 'react';

import { Button } from '@/components/base/buttons/button';
import { useResendVerificationEmail } from '@/hooks/api/use-resend-verification-email';
import { AuthLayout } from '@/routes/participants/-components/auth-layout';
import { DecorativeCircles } from '@/routes/participants/-components/decorative-circles';
import { ResearcherRightPanel } from '@/routes/researchers/-auth/right-panel';
import { getErrorMessage } from '@/utils/error';

/**
 * "Check your email" landing page shown to researchers immediately after
 * signup. Mirrors the participant equivalent at `/participants/verify-email`
 * but uses the researcher right-panel and routes errors / success back into
 * the researcher app.
 *
 * The actual verification happens at
 * `/researchers/verify-email/$userId/account/$token` when the user clicks
 * the link in their email. That route creates the session and redirects
 * into the researcher onboarding flow.
 */
export const Route = createFileRoute('/researchers/verify-email/')({
  beforeLoad: ({ context }) => {
    const { user } = context;
    if (!user) throw redirect({ to: '/researchers/login' });

    if (!user.roles.includes('researcher'))
      throw redirect({ to: '/participants' });

    if (user.isEmailVerified) throw redirect({ to: '/researchers/onboarding' });
    return { user };
  },
  component: ResearcherVerifyEmailPage,
});

function ResearcherVerifyEmailPage() {
  const { user } = Route.useRouteContext();
  const email = user.email;

  return (
    <AuthLayout rightPanel={<ResearcherRightPanel />}>
      <div className="flex flex-col items-center text-center">
        <DecorativeCircles>
          <span className="text-display-sm leading-none">{'\u{1F4EB}'}</span>
        </DecorativeCircles>

        <h1 className="z-10 mt-2 font-heading text-display-sm font-semibold text-primary">
          <Trans>Check your email</Trans>
        </h1>

        <p className="z-10 mt-3 text-md text-primary">
          <Trans>
            We&apos;ve sent a verification link to{' '}
            <span className="font-bold">{email}</span>. Click the link in that
            email to finish setting up your researcher workspace.
          </Trans>
        </p>

        <p className="mt-8 mb-8 text-sm text-tertiary">
          <Trans>
            Double-check your spam or junk folder if you don&apos;t see the
            email within a few minutes.
          </Trans>
        </p>

        <ResendLink />

        <Link to="/sign-out" className="mt-6 text-sm text-tertiary">
          <Trans>Use a different account</Trans>
        </Link>
      </div>
    </AuthLayout>
  );
}

const COOLDOWN_SECONDS = 60;

function ResendLink() {
  const { t } = useLingui();
  const [cooldown, setCooldown] = useState(0);
  const resendMutation = useResendVerificationEmail();

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const handleResend = () => {
    if (resendMutation.isPending || cooldown > 0) return;
    resendMutation.mutate(undefined, {
      onSuccess: () => setCooldown(COOLDOWN_SECONDS),
    });
  };

  const resendError = resendMutation.isError
    ? (getErrorMessage(resendMutation.error) ??
      t`Failed to resend email. Please try again later.`)
    : undefined;

  return (
    <div className="flex flex-col items-center gap-2">
      {cooldown > 0 && (
        <span className="inline-flex items-center gap-1 font-semibold text-success-primary">
          <CheckCircle className="size-4" />
          <Trans>Email sent!</Trans>
        </span>
      )}
      <Button
        color="link-color"
        size="sm"
        isLoading={resendMutation.isPending}
        isDisabled={cooldown > 0}
        onClick={handleResend}
      >
        {cooldown > 0 ? (
          <Trans>Resend in {cooldown}s</Trans>
        ) : (
          <Trans>Resend email</Trans>
        )}
      </Button>
      {resendMutation.isError && (
        <span className="inline-flex items-center gap-1 text-sm text-error-primary">
          <AlertCircle className="inline size-4" />
          {resendError}
        </span>
      )}
    </div>
  );
}
